/**
 * LiveKit client conventions (must match backend).
 *
 * Room:     session:{sessionId}
 * Identity: bare user id (role is in participant metadata JSON)
 * URL:      always from token response `url` (per-session SFU, e.g. wss://s73…).
 *           REACT_APP_LIVEKIT_URL is optional emergency fallback only — leave empty.
 */

export type LiveSessionRole = "trainer" | "student";

export function getLiveKitServerUrl(): string | undefined {
  const url = (process.env.REACT_APP_LIVEKIT_URL || "").trim();
  return url || undefined;
}

/**
 * Token response carries the per-session LiveKit WSS URL.
 * Do not hardcode a shared SFU — each session has its own host.
 */
export function isLiveKitConfigured(): boolean {
  return true;
}

/** Canonical room name — matches backend build_livekit_room_name. */
export function buildLiveKitRoomName(sessionId: string | number): string {
  return `session:${sessionId}`;
}

/** Identity is the bare student_id / trainer_id (role is in LiveKit metadata). */
export function buildLiveKitIdentity(
  _role: LiveSessionRole,
  userId: string
): string {
  return String(userId || "").trim();
}

/**
 * Resolve trainer vs student from metadata first, then legacy identity prefixes.
 */
export function parseParticipantRole(
  metadata: string | undefined,
  identity: string
): LiveSessionRole | null {
  try {
    const parsed = JSON.parse(metadata || "{}") as { role?: string };
    if (parsed.role === "trainer" || parsed.role === "student") {
      return parsed.role;
    }
  } catch {
    // ignore invalid metadata
  }
  // Legacy tokens used role-prefixed identities.
  if (identity.startsWith("trainer-")) return "trainer";
  if (identity.startsWith("student-")) return "student";
  return null;
}

/** LiveKit egress recorder (hidden student-view capture bot). */
export function isRecorderParticipant(
  identity: string | undefined,
  metadata?: string,
): boolean {
  const id = String(identity || "");
  if (!id) return false;
  const lower = id.toLowerCase();
  if (id.startsWith("EG_") || lower.startsWith("eg_") || lower.includes("egress")) {
    return true;
  }
  try {
    const parsed = JSON.parse(metadata || "{}") as { role?: string };
    if (parsed.role === "recorder") return true;
  } catch {
    // ignore invalid metadata
  }
  return false;
}

/** Waiting-room flag from LiveKit participant metadata. */
export function isParticipantWaiting(metadata: string | undefined): boolean {
  try {
    const parsed = JSON.parse(metadata || "{}") as { waiting?: boolean };
    return Boolean(parsed.waiting);
  } catch {
    return false;
  }
}

/** Presenter (screen-share allowed) from metadata. */
export function isParticipantPresenter(metadata: string | undefined): boolean {
  try {
    const parsed = JSON.parse(metadata || "{}") as { presenter?: boolean };
    return Boolean(parsed.presenter);
  } catch {
    return false;
  }
}

/** Host force-muted mic (student cannot unmute until Allowed). */
export function isParticipantMicLocked(metadata: string | undefined): boolean {
  try {
    const parsed = JSON.parse(metadata || "{}") as { mic_locked?: boolean };
    return Boolean(parsed.mic_locked);
  } catch {
    return false;
  }
}

/** Host force-disabled camera until Allowed. */
export function isParticipantCamLocked(metadata: string | undefined): boolean {
  try {
    const parsed = JSON.parse(metadata || "{}") as { cam_locked?: boolean };
    return Boolean(parsed.cam_locked);
  } catch {
    return false;
  }
}

export function isLiveKitSession(session: {
  session_provider?: string | null;
  sessionProvider?: string | null;
}): boolean {
  const provider = (
    session.session_provider ||
    session.sessionProvider ||
    ""
  )
    .toString()
    .trim()
    .toLowerCase();
  return provider === "livekit";
}
